import { Suspense, useState } from 'react'
import { ThemeProvider } from '@mui/material/styles'
import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import theme from 'styles/material-theme'
import { privateRoutes, publicRoutes } from './routes'
import { SimulationAuthProvider } from 'contexts/SimulationAuthContext'
import { createBrowserRouter, Outlet, RouterProvider } from 'react-router-dom'
import { AuthProvider } from 'contexts/auth'
import { PopupProvider } from 'contexts/PopupContext'
import PublicidadeAlert from 'pages/publicity/components/PublicidadeAlert'
import { LinearProgress, styled } from '@mui/material'
import { SnackbarProvider, useSnackbarAlertContext } from 'contexts/SnackbarContext'
import { ErrorResponse, HandlerErrorMessage } from 'utils/HandlerErrorMessage'
import { AxiosError } from 'axios'
import ErrorBoundary from 'components/Custom/ErrorBoundary'

const LoaderWrapper = styled('div')(() => ({
  position: 'fixed',
  top: 0,
  left: 0,
  zIndex: 2001,
  width: '100%',
  '& > * + *': {
    marginTop: 0
  }
}))

// ==============================|| Loader ||============================== //
const Loader = () => (
  <LoaderWrapper>
    <LinearProgress color="primary" />
  </LoaderWrapper>
)

const Layout = () => {
  return (
    <AuthProvider>
      <PopupProvider>
        <ErrorBoundary>
          <PublicidadeAlert />
          <Suspense fallback={<Loader />}>
            <Outlet />
          </Suspense>
        </ErrorBoundary>
      </PopupProvider>
    </AuthProvider>
  )
}

const wrappedPrivateRoutes = privateRoutes.map((route) => {
  if (route.path.startsWith('/simulacao')) {
    return {
      ...route,
      element: <SimulationAuthProvider>{route.element}</SimulationAuthProvider>
    }
  }
  return route
})

const routes = [
  {
    path: '/',
    element: <Layout />,
    children: [...publicRoutes, ...wrappedPrivateRoutes],
    errorElement: (
      <ErrorBoundary fallback={<Loader />}>
        <Outlet />
      </ErrorBoundary>
    )
  }
]

const router = createBrowserRouter(routes)

const AppContent = () => {
  const { showAlert } = useSnackbarAlertContext()
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: false
          }
        },
        queryCache: new QueryCache({
          onError: (error) => {
            const err = error as AxiosError<ErrorResponse>
            HandlerErrorMessage({ error: err, showAlert })
          }
        })
      })
  )

  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} fallbackElement={<Loader />} />
    </QueryClientProvider>
  )
}

const App = () => {
  return (
    <ThemeProvider theme={theme}>
      <SnackbarProvider>
        <ErrorBoundary>
          <AppContent />
        </ErrorBoundary>
      </SnackbarProvider>
    </ThemeProvider>
  )
}

export default App
